/**
 * Deep-link Email : ?uid=&accountId=&folder=
 * Handoff hub (même origine) : sessionStorage `aurelia_mail_deeplink`
 */

const STORAGE_KEY = "aurelia_mail_deeplink";
const MAX_AGE_MS = 60_000;

export type MailDeepLink = {
  uid: number | null;
  accountId: number | null;
  folder: string;
};

function empty(): MailDeepLink {
  return { uid: null, accountId: null, folder: "INBOX" };
}

function parseUid(raw: string | null | undefined): number | null {
  const n = parseInt(String(raw ?? "").trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parseAccountId(raw: string | null | undefined): number | null {
  const n = parseInt(String(raw ?? "").trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function fromSearch(): MailDeepLink {
  if (typeof window === "undefined") return empty();
  try {
    const q = new URLSearchParams(window.location.search || "");
    return {
      uid: parseUid(q.get("uid")),
      accountId: parseAccountId(q.get("accountId")),
      folder: String(q.get("folder") || "INBOX").trim() || "INBOX",
    };
  } catch {
    return empty();
  }
}

function fromSession(): MailDeepLink {
  if (typeof window === "undefined" || !window.sessionStorage) return empty();
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return empty();
    const o = JSON.parse(raw) as {
      uid?: string;
      accountId?: string;
      folder?: string;
      ts?: number;
    };
    const ts = Number(o?.ts || 0);
    if (!Number.isFinite(ts) || Date.now() - ts > MAX_AGE_MS) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return empty();
    }
    window.sessionStorage.removeItem(STORAGE_KEY);
    return {
      uid: parseUid(o?.uid),
      accountId: parseAccountId(o?.accountId),
      folder: String(o?.folder || "INBOX").trim() || "INBOX",
    };
  } catch {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    return empty();
  }
}

/** Lit query puis handoff sessionStorage. */
export function readMailDeepLink(): MailDeepLink {
  const url = fromSearch();
  if (url.uid != null) return url;
  return fromSession();
}

/** Retire uid/accountId/folder de l’URL après application. */
export function clearMailDeepLinkFromUrl(): void {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    let changed = false;
    for (const key of ["uid", "accountId", "folder"]) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    }
    if (!changed) return;
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState(window.history.state, "", next);
  } catch {
    /* ignore */
  }
}
